import { graphql } from 'react-relay';
import React, { useCallback, Fragment } from 'react';
import type { FC } from 'react';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import type { ApolloQueryResult } from '@confluence/apollo-relay-interop/entry-points/apolloTypes.ts';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { NetworkStatus } from '@confluence/apollo-relay-interop/entry-points/NetworkStatus.ts';

import { fg } from '@confluence/feature-gating';

import type {
	FilterByLabelFeedQuery as FilterByLabelFeedQuery$data,
	FilterByLabelFeedQueryVariables as FilterByLabelFeedQuery$variables,
	FilterByLabelFeedQuery as FilterByLabelFeedQueryType,
} from './__types__/FilterByLabelFeedQuery.ts';
import { FeedCardsWrapper } from './FeedCards/FeedCardsWrapper.tsx';
import { FeedType } from './useFeedTypeSelector.ts';
import { PopularFeedCard } from './FeedCards/PopularFeedCard/index.ts';
import { ExitFeedAllyButton } from './FeedCards/ExitFeedAllyButton.tsx';
// oxlint-disable-next-line graphql-relay-compat/no-interop-imports -- see https://go.atlassian.com/cfe-no-interop-imports
import { useQueryInterop } from '@confluence/apollo-relay-interop/entry-points/useQueryInterop.ts';
const FilterByLabelFeedQuery = graphql`
	query FilterByLabelFeedQuery($cql: String!, $first: Int, $after: String) {
		search: confluence_search(cql: $cql, first: $first, after: $after) {
			nodes {
				content {
					title
					id
					type
					subType
					emoji: properties(key: "emoji-title-published") {
						nodes {
							id
							value
						}
					}
					excerpt(length: 350)
					labels {
						nodes {
							name
						}
					}
					links {
						webui
					}
					space {
						id
						key
						name
					}
					history {
						createdDate
						createdBy {
							type
							displayName
							profilePicture {
								path
							}
							... on KnownUser {
								accountId
							}
							... on UnknownUser {
								accountId
							}
						}
						ownedBy {
							type
							displayName
							profilePicture {
								path
							}
							... on KnownUser {
								accountId
							}
						}
					}
					metadata {
						lastModifiedDate
						comments {
							commentsCount
						}
						currentuser {
							favourited {
								isFavourite
							}
						}
					}
					version {
						friendlyWhen
					}
				}
			}
			pageInfo {
				hasNextPage
				endCursor
			}
		}
	}
`;
type FilterByLabelFeedProps = {
	filterByLabel: string;
	handleFocusRef: () => void;
};

export const FilterByLabelFeed: FC<FilterByLabelFeedProps> = ({
	filterByLabel,
	handleFocusRef,
}) => {
	const { data, loading, error, networkStatus, fetchMore } = useQueryInterop<
		FilterByLabelFeedQuery$data,
		FilterByLabelFeedQuery$variables
	>(FilterByLabelFeedQuery, {
		variables: {
			cql: `label = "${filterByLabel}" AND created >= now(\"-2w\") order by created desc`,
			// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
			first: fg('confluence_ai_feeds_home_banner') ? 10 : 5,
		},
		errorPolicy: 'all',
		notifyOnNetworkStatusChange: true,
	});
	const results = data?.search?.nodes || [];
	// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
	const hasNextPage = fg('confluence_ai_feeds_home_banner')
		? false
		: data?.search?.pageInfo?.hasNextPage || false;

	// Must convert `endCursor` to `string | undefined`; never null.
	const endCursor = data?.search?.pageInfo?.endCursor ?? undefined;

	const disableAnimation = Boolean(
		// eslint-disable-next-line check-react-ssr-usage/no-react-ssr
		process.env.REACT_SSR || (window.__SSR_RENDERED__ && networkStatus !== NetworkStatus.fetchMore),
	);

	const feedType = filterByLabel === 'welcome-blog' ? FeedType.WELCOME : FeedType.FAREWELL;

	const loadNextPage = useCallback(async (): Promise<
		ApolloQueryResult<FilterByLabelFeedQueryType> | undefined
	> => {
		try {
			if (!endCursor) {
				return undefined;
			}

			const newResult = await fetchMore({
				variables: {
					first: 5,
					after: endCursor,
				},
				updateQuery: (prevResults: FilterByLabelFeedQueryType, { fetchMoreResult }) => {
					if (!fetchMoreResult) {
						return prevResults;
					}
					if (fetchMoreResult?.search?.nodes && prevResults?.search?.nodes) {
						fetchMoreResult.search.nodes = prevResults.search.nodes.concat(
							fetchMoreResult.search.nodes,
						);
					}

					return fetchMoreResult;
				},
			});

			if (newResult.errors) {
				throw newResult.errors;
			}

			return newResult;
		} catch (e) {
			throw e;
		}
	}, [fetchMore, endCursor]);

	// oxlint-disable-next-line confluence-feature-gating/no-new-feature-gates
	const feedItems = fg('confluence_ai_feeds_home_banner') ? results.slice(0, 3) : results;

	// The `endCursor` *must* be passed in, otherwise the `InfiniteScroll` component paginates at most *once*.
	return (
		<FeedCardsWrapper
			loading={loading}
			firstPageError={error}
			networkStatus={networkStatus}
			endCursor={endCursor}
			hasNextPage={hasNextPage}
			loadNextPage={loadNextPage}
			feedType={feedType}
		>
			{feedItems.map((result, index) => {
				return (
					result && (
						<Fragment key={result.content?.id ?? index}>
							<PopularFeedCard
								feedItem={result}
								ranking={index}
								disableAnimation={disableAnimation}
							/>
							<ExitFeedAllyButton
								onExit={handleFocusRef}
								source="popular-feed"
								feedCardIndex={index}
							/>
						</Fragment>
					)
				);
			})}
		</FeedCardsWrapper>
	);
};
